/**
 * Coherency refusal rendering — shared between knowledge.ts and smart-capture.ts.
 * Extracted to break the knowledge ↔ smart-capture circular dependency (WP-465 TEN-2458).
 *
 * Display-lane only: never interprets the refusal, only renders it.
 * The contract lives in convex/lib/gates/coherencyControls.ts (CoherencyRefusal).
 */

import { failureResult } from "../envelope.js";

/** Structured coherency refusal (WP-465 slice ⑤ Option B) — rendered, never interpreted here. */
export type CoherencyRefusalPayload = {
  reason?: string;
  disposition?: string;
  overridable?: boolean;
  mode?: string;
  subject?: { entryId?: string | null; name?: string };
  routes?: Record<string, unknown>;
};

/** Options controlling how the refusal is rendered (vs. interpreted — interpretation never happens here). */
export type CoherencyRefusalRenderOptions = {
  /**
   * Markdown heading level for the leading "Write refused…" line. Defaults to 1 (H1) for the
   * standalone failure result; embedding callers (e.g. under an H2 `## Accept failed`) pass 3
   * so the hierarchy stays correct (no H1 nested under an H2).
   */
  headingLevel?: 1 | 2 | 3;
};

/** Coerce a possibly-non-string description to a clean string, or undefined if not renderable. */
function asDescription(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

const GENERIC_RECOVERY_LINE =
  "Respond and retry: link the entry to the strategic spine, pass `coherencyAcknowledgement`, or supply a typed `steeringOverrideReason`.";

/**
 * Build a failure ToolResult from a structured coherency refusal, rendering the actual
 * routes (link / acknowledge / override) and mode from the payload.
 *
 * Degrades gracefully when routes are absent OR present-but-empty (no recognized route yields a
 * bullet) — falls back to a generic "use one of the routes" sentence so the agent never hits a
 * dead end. The fallback is keyed on whether ≥1 route bullet was actually emitted, not on the
 * raw key count, so `{ link: null }` or unknown-only keys still leave a way forward.
 */
export function coherencyRefusedResult(
  toolName: string,
  entryId: string,
  refusal: CoherencyRefusalPayload,
  options?: CoherencyRefusalRenderOptions,
) {
  const headingLevel = options?.headingLevel ?? 1;
  const heading = "#".repeat(headingLevel);
  const lines: string[] = [
    `${heading} Write refused by the coherency gate`,
    "",
    refusal.reason ?? `The coherency gate refused this write on ${entryId}.`,
  ];

  if (refusal.subject?.name) {
    lines.push(`On: ${refusal.subject.name}`);
  }

  if (refusal.mode) {
    lines.push("", `Mode: ${refusal.mode}`);
  }

  const routes = refusal.routes as Record<string, unknown> | undefined;
  const routeKeys = routes ? Object.keys(routes) : [];
  if (routeKeys.length > 0) {
    // Subheading stays exactly one level below the leading heading so an embedded caller
    // (headingLevel: 3) renders `### Write refused…` / `#### Recovery routes`, never an
    // inverted `## Recovery routes` nested under an `###` (codex/self review).
    lines.push("", `${"#".repeat(headingLevel + 1)} Recovery routes`);
    // MINOR 3: track whether any route actually rendered a bullet — the no-dead-end guarantee
    // must hold even when routes is present but yields nothing renderable.
    const beforeRoutes = lines.length;
    if (routes && typeof routes.link === "object" && routes.link !== null) {
      const link = routes.link as { description?: unknown };
      lines.push(`- **link:** ${asDescription(link.description) ?? "Link the entry to the strategic spine."}`);
    }
    if (routes && typeof routes.acknowledge === "object" && routes.acknowledge !== null) {
      const ack = routes.acknowledge as { param?: string; responses?: ReadonlyArray<string>; minReasonChars?: number; description?: unknown };
      const responses = ack.responses?.join(" | ") ?? "linked | accepted-fix | diverged";
      lines.push(
        `- **acknowledge** (\`${ack.param ?? "coherencyAcknowledgement"}\`): ${responses}${ack.minReasonChars ? ` (≥${ack.minReasonChars} chars for diverged)` : ""}. ${asDescription(ack.description) ?? ""}`.trim(),
      );
    }
    if (routes && typeof routes.override === "object" && routes.override !== null) {
      const ov = routes.override as { param?: string; minChars?: number; description?: unknown };
      lines.push(
        `- **override** (\`${ov.param ?? "steeringOverrideReason"}\`): ≥${ov.minChars ?? 12} chars. ${asDescription(ov.description) ?? ""}`.trim(),
      );
    }
    if (lines.length === beforeRoutes) {
      // Header printed but no bullet followed → append the generic fallback so the section
      // never dead-ends under a bare "## Recovery routes".
      lines.push(GENERIC_RECOVERY_LINE);
    }
  } else {
    lines.push("", GENERIC_RECOVERY_LINE);
  }

  return failureResult(
    lines.join("\n"),
    "COHERENCY_REFUSED",
    `Coherency gate refused ${toolName} on ${entryId}.`,
    "Use one of the refusal's routes (link / coherencyAcknowledgement / steeringOverrideReason), then retry.",
    undefined,
    { entryId, refusal },
  );
}
